import type { AppPlatform } from '@/types/constants';

/**
 * Base class that defines a platform bridge containing exposed integration commands
 *
 * @prop platform {String}          Compatible platform name
 * @prop commands {[String]}        List of commands exposed by bridge
 * @prop bridge {Object}            Platform bridge object with integration methods
 * @method exec(params)                                 Checks for functions existance before calling it
 * @method isCompatible {function(): Boolean}           Performs bridge health checks
 * @method isAvailableFor {function(String): Boolean}   Validates platform and compatibility
 * @method debug {function(): void}                     Performs debug checks for bridge
 */

export abstract class PlatformBridgeBase {
    protected platform: AppPlatform | null = null;
    protected bridge: any = null;
    protected bridgeCommands: string[] = [];

    protected constructor() {
        if (new.target === PlatformBridgeBase) {
            throw new TypeError('BasePlatformBridge class can NOT be instantiated directly');
        }
    }

    public exec(command: string, ...params: any[]) {
        if (!this.bridge) {
            console.error(`Bridge for platform ${this.platform} is undefined!! Skipping command`);
        }

        const method = this[command as keyof this];

        if (typeof method === 'function') {
            return method.apply(this, params);
        }

        console.warn(`Unknown Method: ${command.toString()} for platform bridge ${this.platform}`);
    }

    public isCompatible = () => false;

    public isAvailableFor(platform: AppPlatform) {
        try {
            return this.platform === platform && !!this.isCompatible();
        } catch {
            return false;
        }
    }

    public debug() {
        if (!this.bridge) {
            console.warn(`Bridge not found for ${this.platform} platform`);
        } else {
            this.bridgeCommands.forEach((cmd) => {
                if (!(this.bridge.hasOwnProperty(cmd) && typeof this.bridge[cmd] === 'function')) {
                    console.warn(`Method ${cmd.toString()} not implemented for platform ${this.platform} on bridge`);
                }
            });
        }
    }
}
